import { useTheme, type ThemeColor } from '@bct/styleguide';
import { useEffect } from 'react';
import { BCT_THEMES, DEFAULT_BCT_THEME, useAuthStore } from '@/stores/auth';

const VALID_THEMES: readonly ThemeColor[] = BCT_THEMES;
const DEFAULT_THEME: ThemeColor = DEFAULT_BCT_THEME;

/**
 * Syncs the user's organization theme to the styleguide's ThemeProvider.
 *
 * The backend returns organizationTheme based on:
 * 1. Organization.bctTheme (multi-org mode)
 * 2. DEFAULT_BCT_THEME env variable (single-tenant mode)
 *
 * This component ensures the styleguide theme stays in sync.
 */
export function ThemeSync() {
  const { user } = useAuthStore();
  const { setCurrentTheme, currentTheme } = useTheme();

  useEffect(() => {
    // Determine target theme from user's organization, falling back to the
    // default. An unknown value used to leave the theme untouched, so whatever
    // was set last stayed on screen — for a fresh session that is the provider's
    // own default, which is not a decision anyone made.
    const organizationTheme = user?.organizationTheme as ThemeColor | undefined;
    const targetTheme =
      organizationTheme && VALID_THEMES.includes(organizationTheme)
        ? organizationTheme
        : DEFAULT_THEME;

    if (currentTheme !== targetTheme) {
      setCurrentTheme(targetTheme);
    }
  }, [user?.organizationTheme, currentTheme, setCurrentTheme, user]);

  return null;
}
